import React, { useState } from 'react';
import { useMutation } from 'react-query';
import { GroupedErrorMessages, RabbitMQAction, RabbitMQErrorMessage, supportsRequeueIfNeeded } from 'types/rabbitMQ';
import { sendEditedRabbitMQMessage } from 'utils/ts-queries';
import { buildExceptionTypeGroups, ExceptionTypeGroup } from './exceptionGrouping';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import Tooltip from '@mui/material/Tooltip';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import FormControlLabel from '@mui/material/FormControlLabel';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import JsonViewer from 'components/Shared/JsonViewer';
import EditMessageDialog from './EditMessageDialog';

type Props = {
  groupedMessages: GroupedErrorMessages[];
  onActionChange: (groupIndex: number, messageIndexes: number[], action: RabbitMQAction) => void;
  showEmptyState?: boolean;
};

type ActionRadioGroupProps = {
  value: RabbitMQAction | '';
  onChange: (action: RabbitMQAction) => void;
  allowRequeueIfNeeded?: boolean;
};

function ActionRadioGroup({ value, onChange, allowRequeueIfNeeded = false }: ActionRadioGroupProps) {
  return (
    <RadioGroup
      row
      value={value}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => onChange(e.target.value as RabbitMQAction)}
      sx={{ mr: 2, flexShrink: 0 }}
    >
      <FormControlLabel
        value="none"
        control={<Radio size="small" onClick={(e) => e.stopPropagation()} />}
        label="Rien"
      />
      <FormControlLabel
        value="requeue"
        control={<Radio size="small" color="success" onClick={(e) => e.stopPropagation()} />}
        label="Rejouer"
      />
      {allowRequeueIfNeeded && (
        <FormControlLabel
          value="requeue-if-needed"
          control={<Radio size="small" color="success" onClick={(e) => e.stopPropagation()} />}
          label={
            <Tooltip title="Rejoue uniquement si la pièce n'a pas été mise à jour depuis l'erreur (évite d'écraser des données plus récentes)">
              <span>Rejouer si besoin</span>
            </Tooltip>
          }
        />
      )}
      <FormControlLabel
        value="delete"
        control={<Radio size="small" color="error" onClick={(e) => e.stopPropagation()} />}
        label="Supprimer"
      />
    </RadioGroup>
  );
}

/** Action commune à tous les messages du groupe de type, ou '' si les actions divergent. */
function getCommonAction(typeGroup: ExceptionTypeGroup): RabbitMQAction | '' {
  const first = typeGroup.items[0]?.message.action || 'none';
  const allSame = typeGroup.items.every(item => (item.message.action || 'none') === first);
  return allSame ? first : '';
}

function MessageAccordion({ groupedMessages, onActionChange, showEmptyState = true }: Props) {
  const [expanded, setExpanded] = useState<string | false>(false);
  const [expandedType, setExpandedType] = useState<string | false>(false);
  const [expandedMessage, setExpandedMessage] = useState<string | false>(false);
  const [copyTooltip, setCopyTooltip] = useState<string>("Copier le message");
  const [editTarget, setEditTarget] = useState<RabbitMQErrorMessage | null>(null);
  const [notification, setNotification] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  const sendEditedMutation = useMutation(sendEditedRabbitMQMessage, {
    onSuccess: (data) => {
      setNotification({ open: true, message: data.message, severity: 'success' });
      setEditTarget(null);
    },
    onError: (err: any) => {
      setNotification({
        open: true,
        message: `Erreur lors de l'envoi du message édité : ${err?.message || err}`,
        severity: 'error'
      });
    }
  });

  const handleChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handleTypeChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedType(isExpanded ? panel : false);
  };

  const handleMessageChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedMessage(isExpanded ? panel : false);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        setCopyTooltip("Copié !");
        setTimeout(() => setCopyTooltip("Copier le message"), 2000);
      })
      .catch(err => {
        console.error('Erreur lors de la copie :', err);
        setCopyTooltip("Erreur lors de la copie");
        setTimeout(() => setCopyTooltip("Copier le message"), 2000);
      });
  };

  const renderMessageDetails = (message: RabbitMQErrorMessage) => (
    <Box sx={{ width: '100%', pl: 2 }}>
      {message.message && (
        <>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            mt: 1
          }}>
            <Typography
              variant="body2"
              component="div"
              sx={{
                fontWeight: 'bold',
                mr: 1
              }}
            >
              Message:
            </Typography>
            <Tooltip title={copyTooltip}>
              <IconButton
                size="small"
                onClick={() => copyToClipboard(message.message.replace(/"/g, '\\"') || '')}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Éditer puis envoyer sur la queue d'origine">
              <span>
                <IconButton
                  size="small"
                  onClick={() => setEditTarget(message)}
                  disabled={!message.queue}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          <Box sx={{ mt: 1 }}>
            <JsonViewer
              json={message.message ? message.message.replace(/\\"/g, '"') : '{}'}
              maxHeight="300px"
            />
          </Box>
        </>
      )}

      {message.exceptionMessage && (
        <>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            mt: 2
          }}>
            <Typography
              variant="body2"
              component="div"
              sx={{
                fontWeight: 'bold',
                mr: 1
              }}
            >
              Exception:
            </Typography>
            <Tooltip title={copyTooltip}>
              <IconButton
                size="small"
                onClick={() => copyToClipboard(message.exceptionMessage)}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Typography
            variant="body2"
            component="div"
            sx={{
              p: 1,
              borderRadius: 1,
              whiteSpace: 'pre-wrap',
              fontFamily: 'monospace',
              mb: 1
            }}
          >
            {message.exceptionMessage}
          </Typography>
        </>
      )}

      {message.exceptionStackTrace && (
        <>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            mt: 1
          }}>
            <Typography
              variant="body2"
              component="div"
              sx={{
                fontWeight: 'bold',
                mr: 1
              }}
            >
              Stack trace:
            </Typography>
            <Tooltip title={copyTooltip}>
              <IconButton
                size="small"
                onClick={() => copyToClipboard(message.exceptionStackTrace)}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Typography
            variant="body2"
            component="div"
            sx={{
              p: 1,
              borderRadius: 1,
              maxHeight: '300px',
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              fontFamily: 'monospace',
              mb: 2
            }}
          >
            {message.exceptionStackTrace.replace(/ at /g, '\tat ')}
          </Typography>
        </>
      )}
    </Box>
  );

  return (
    <div>
      {groupedMessages.length === 0 ? (
        showEmptyState ? <Typography>Aucun message d'erreur trouvé</Typography> : null
      ) : (
        groupedMessages.map((group, groupIndex) => {
          const allowRequeueIfNeeded = supportsRequeueIfNeeded(group.routingKey);
          return (
          <Accordion
            key={group.routingKey}
            expanded={expanded === group.routingKey}
            onChange={handleChange(group.routingKey)}
            TransitionProps={{ mountOnEnter: true, unmountOnExit: true }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ width: '60%', flexShrink: 0 }}>
                {group.routingKey || 'Routing key inconnue'}
              </Typography>
              <Chip
                label={`${group.count} message${group.count > 1 ? 's' : ''}`}
                color="primary"
                size="small"
              />
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              {buildExceptionTypeGroups(group.messages).map((typeGroup) => {
                const typePanelId = `${group.routingKey}::${typeGroup.signature}`;
                return (
                  <Accordion
                    key={typePanelId}
                    expanded={expandedType === typePanelId}
                    onChange={handleTypeChange(typePanelId)}
                    TransitionProps={{ mountOnEnter: true, unmountOnExit: true }}
                    sx={{ '&:before': { display: 'none' } }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', minWidth: 0 }}>
                        <ActionRadioGroup
                          value={getCommonAction(typeGroup)}
                          allowRequeueIfNeeded={allowRequeueIfNeeded}
                          onChange={(action) => onActionChange(
                            groupIndex,
                            typeGroup.items.map(item => item.index),
                            action
                          )}
                        />
                        <Box sx={{ flexGrow: 1, minWidth: 0, mr: 2 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {typeGroup.classes}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              fontFamily: 'monospace',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            {typeGroup.excerpt}
                          </Typography>
                        </Box>
                        <Chip
                          label={`${typeGroup.items.length} message${typeGroup.items.length > 1 ? 's' : ''}`}
                          color="secondary"
                          size="small"
                          sx={{ flexShrink: 0 }}
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0, pl: 2 }}>
                      {typeGroup.items.map((item) => {
                        const panelId = `${group.routingKey}-${item.index}`;
                        const message = item.message;
                        return (
                          <Accordion
                            key={panelId}
                            expanded={expandedMessage === panelId}
                            onChange={handleMessageChange(panelId)}
                            TransitionProps={{ mountOnEnter: true, unmountOnExit: true }}
                            sx={{ '&:before': { display: 'none' } }}
                          >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Box sx={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                                <ActionRadioGroup
                                  value={message.action || 'none'}
                                  allowRequeueIfNeeded={allowRequeueIfNeeded}
                                  onChange={(action) => onActionChange(groupIndex, [item.index], action)}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{ flexGrow: 1, fontFamily: 'monospace' }}
                                >
                                  {message.id || 'Id inconnu'}
                                </Typography>
                                <Typography variant="body2" component="span" sx={{ ml: 2 }}>
                                  {message.dateTime ? new Date(message.dateTime).toLocaleString() : ''}
                                </Typography>
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                              {renderMessageDetails(message)}
                            </AccordionDetails>
                          </Accordion>
                        );
                      })}
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </AccordionDetails>
          </Accordion>
          );
        })
      )}

      <EditMessageDialog
        open={editTarget !== null}
        queue={editTarget?.queue || ''}
        initialPayload={editTarget?.message || ''}
        isSending={sendEditedMutation.isLoading}
        onClose={() => setEditTarget(null)}
        onSend={(payload) => {
          if (!editTarget) return;
          sendEditedMutation.mutate({ queue: editTarget.queue, payload });
        }}
      />

      <Snackbar
        open={notification.open}
        autoHideDuration={5000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setNotification(prev => ({ ...prev, open: false }))}
          severity={notification.severity}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default MessageAccordion;
